import { Alert, Box, Button, Checkbox, Container, Dialog, DialogActions, DialogContent, Divider, List, ListItem, ListItemText, Paper, Typography } from "@mui/material";
import { useParams } from "react-router-dom"
import TopBar from "../../components/TopBar";
import BottomBar from "../../components/BottomBar";
import { useMemo, useState } from "react";

import { PRODUCT_NAMES, PRODUCT_IMAGES } from "../../components/ProductDetails";

const DOWNLOAD_BACKGROUND = "linear-gradient(145deg, #f5f4f1 0%, #d9dadd 52%, #b8bac0 100%)";

export const SUGGESTED_PRODUCT_IDS = [
  "rev-f-amplifier-suite",
  "distant-amplifier-suite",
  "dd-amplifier-suite",
  "distant-bass-suite",
  "protone-monsterfuzz",
  "protone-deadhorse",
  "protone-midboost",
];


const Embargo = ({ handleAgree }) => {
  const [agreed, setAgreed] = useState(false);

  return <Dialog maxWidth="md" sx={{ py: 4 }} open>
    <DialogContent>
      <Typography variant="h3" gutterBottom>
        Beta Program Terms of Service
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        Effective: August 9th, 2025
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Quick Summary</Typography>
        <List>
          <ListItem><ListItemText primary="Private access — Invite-only beta." /></ListItem>
          <ListItem><ListItemText primary="No public sharing until embargo lifts." /></ListItem>
          <ListItem><ListItemText primary="Provide feedback to improve the product." /></ListItem>
          <ListItem><ListItemText primary="Expect bugs — software may break or cause data loss." /></ListItem>
          <ListItem><ListItemText primary="Follow rules — misconduct may lead to removal." /></ListItem>
        </List>
      </Paper>

      <Box>
        <Typography variant="h6">1. Eligibility</Typography>
        <Typography>Participation is by invitation only and may be revoked at the WaveMind's discretion.</Typography>

        <Typography variant="h6" mt={2}>2. Purpose</Typography>
        <Typography>The Beta is provided to allow invited testers to preview the Beta Product and provide feedback.</Typography>

        <Typography variant="h6" mt={2}>3. Embargo & Confidentiality</Typography>
        <Typography>Do not share any details, screenshots, videos, or impressions until the full release or until WaveMind lifts the embargo in writing.</Typography>
        <Typography>All Beta-related information is confidential and proprietary.</Typography>

        <Typography variant="h6" mt={2}>4. Permitted Use</Typography>
        <Typography>You may use the Beta Product for personal, non-commercial testing only. No copying, distributing, modifying, reverse engineering, or derivative works.</Typography>

        <Typography variant="h6" mt={2}>5. Feedback</Typography>
        <Typography>By submitting feedback, you grant WaveMind rights to use it without obligation or compensation.</Typography>

        <Typography variant="h6" mt={2}>6. Risks & Disclaimer</Typography>
        <Typography>The Beta Product is pre-release and provided “AS IS” without warranties. Use at your own risk.</Typography>

        <Typography variant="h6" mt={2}>7. Termination</Typography>
        <Typography>WaveMind may terminate your access at any time. Upon termination, delete all copies of the Beta Product.</Typography>

        <Typography variant="h6" mt={2}>8. Governing Law</Typography>
        <Typography>These Terms are governed by the laws of the Netherlands.</Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" alignItems="center">
        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <Typography>I agree to the Beta Program Terms of Service</Typography>
      </Box>
      <DialogActions>
        <Button variant="contained" color="primary" disabled={!agreed} onClick={handleAgree}>
          I Agree
        </Button>
      </DialogActions>
    </DialogContent>
  </Dialog>
}

const ProductSuggestion = ({ currentProductID, onTrial }) => {
  const suggestedProductID = useMemo(() => {
    const availableProducts = SUGGESTED_PRODUCT_IDS.filter((id) => id !== currentProductID);
    return availableProducts[Math.floor(Math.random() * availableProducts.length)];
  }, [currentProductID]);

  const suggestedProductName = PRODUCT_NAMES[suggestedProductID];

  return <Paper
    component="section"
    elevation={0}
    sx={{
      bgcolor: "rgba(8, 8, 10, 0.96)",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      borderRadius: { xs: 3, md: 5 },
      boxShadow: "0 24px 70px rgba(0, 0, 0, 0.35)",
      color: "rgb(249, 245, 238)",
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr" },
      maxWidth: 1040,
      mx: "auto",
      overflow: "hidden",
      width: "100%",
    }}
  >
    <Box sx={{
      alignItems: "center",
      background: "radial-gradient(circle at center, rgba(249, 245, 238, 0.12), rgba(249, 245, 238, 0.025) 62%, transparent 72%)",
      display: "flex",
      justifyContent: "center",
      minHeight: { xs: 260, md: 390 },
      p: { xs: 2.5, md: 4 },
    }}>
      <Box
        alt={`${suggestedProductName} plugin interface`}
        component="img"
        loading="lazy"
        src={PRODUCT_IMAGES[suggestedProductID]}
        sx={{
          filter: "drop-shadow(0 22px 24px rgba(0, 0, 0, 0.38))",
          maxHeight: { xs: 260, md: 350 },
          maxWidth: "100%",
          objectFit: "contain",
          transition: "transform 240ms ease",
          "&:hover": { transform: "translateY(-4px) scale(1.015)" },
        }}
      />
    </Box>

    <Box sx={{ alignItems: "flex-start", display: "flex", flexDirection: "column", justifyContent: "center", p: { xs: 3, sm: 4, md: 5 }, textAlign: "left" }}>
      <Typography component="p" sx={{ color: "rgba(249, 245, 238, 0.64)", fontSize: "0.75rem !important", fontWeight: 800, letterSpacing: "0.16em", mb: 1.5, textTransform: "uppercase" }}>
        Your next sound awaits
      </Typography>
      <Typography component="h2" variant="h4" sx={{ fontSize: { xs: "1.55rem !important", md: "2rem !important" }, fontWeight: 700, lineHeight: 1.15, mb: 2 }}>
        Try {suggestedProductName}
      </Typography>
      <Typography sx={{ color: "rgba(249, 245, 238, 0.72)", fontSize: "0.95rem !important", lineHeight: 1.65, mb: 3 }}>
        Keep the inspiration going with a free trial. Download it now and discover a different side of your tone.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
        <Button
          onClick={() => onTrial(suggestedProductID)}
          sx={{
            bgcolor: "#f9f5ee",
            color: "#11131a",
            fontWeight: 700,
            px: 3,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { bgcolor: "#ffffff", transform: "translateY(-1px)" },
          }}
          variant="contained"
        >
          Try it now for free
        </Button>
        <Button
          onClick={() => window.open(`/product/${suggestedProductID}`, "_blank")}
          sx={{
            borderColor: "rgba(249, 245, 238, 0.45)",
            color: "#f9f5ee",
            px: 3,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { borderColor: "#f9f5ee", bgcolor: "rgba(249, 245, 238, 0.06)" },
          }}
          variant="outlined"
        >
          Learn more
        </Button>
      </Box>
    </Box>
  </Paper>
}

const DownloadProviderPage = () => {
  const { os, productID } = useParams();
  const isWindowsDownload = os?.toLowerCase() === "windows";
  const productName = PRODUCT_NAMES[productID] || "Your plugin";


  const handleStart = () => {
    const url = `/downloadmanager/${productID}/${os}`
    const handle = window.open(url, "_blank");
    handle?.blur()
    window.focus();
    // postDownload(productID, os).then((response) => {
    //   const url = window.URL.createObjectURL(new Blob([response.data]));
    //   const link = document.createElement('a');
    //   link.href = url;
    //   link.setAttribute('download', response.headers?.["x-file-name"]); //or any other extension
    //   document.body.appendChild(link);
    //   link.click();
    // }).catch((e) => {
    //   dispatch(setError(e?.response?.data?.detail))
    // })
  }

  const handleExtraDownload = (extraID) => {
    const url = `/downloads/${os}/${extraID}`
    const handle = window.open(url, "_blank");
    handle?.blur()
    window.focus();
  }

  return <Box
    sx={{
      background: DOWNLOAD_BACKGROUND,
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      overflow: "hidden",
    }}
  >
    <TopBar />
    <Box
      component="main"
      sx={{
        flex: 1,
        isolation: "isolate",
        overflow: "hidden",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 5, md: 8 },
        position: "relative",
        width: "100%",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: "0 !important", sm: "24px !important" } }}>
        <Paper elevation={0} sx={{
          backdropFilter: "blur(18px)",
          bgcolor: "rgba(20, 20, 23, 0.82)",
          border: "1px solid rgba(249, 245, 238, 0.15)",
          borderRadius: { xs: 3, md: 5 },
          boxShadow: "0 22px 65px rgba(0, 0, 0, 0.32)",
          color: "rgb(249, 245, 238)",
          mb: { xs: 3, md: 4 },
          mx: "auto",
          p: { xs: 3, sm: 3.5, md: 4 },
          textAlign: "center",
        }}>
          <Typography component="p" sx={{ color: "rgba(249, 245, 238, 0.58)", fontSize: "0.75rem !important", fontWeight: 800, letterSpacing: "0.15em", mb: 1.5, textTransform: "uppercase" }}>
            Download started
          </Typography>
          <Typography component="h1" variant="h3" sx={{ fontSize: { xs: "1.7rem !important", md: "2.4rem !important" }, fontWeight: 700, lineHeight: 1.15 }}>
            {productName} is on its way
          </Typography>
          <Typography sx={{ color: "rgba(249, 245, 238, 0.64)", fontSize: "1rem !important", lineHeight: 1.5, mt: 1.5 }}>
            Your installer should begin downloading automatically.
          </Typography>
          {isWindowsDownload && <Alert severity="warning" sx={{
            fontSize: "0.875rem !important",
            lineHeight: 1.2,
            mt: 2.5,
            mx: "auto",
            maxWidth: 620,
            p: "0.25rem 0.5rem",
            textAlign: "left",
            "& .MuiAlert-icon": { fontSize: "1rem", mr: 0.75, py: "0.125rem" },
            "& .MuiAlert-message": { fontSize: "0.875rem !important", lineHeight: 1.2, py: "0.125rem" },
          }}>
            We recently updated our Windows signing certificate. Microsoft SmartScreen may temporarily show a warning when you open the installer.
          </Alert>}
          <Button onClick={handleStart} sx={{ color: "rgba(249, 245, 238, 0.82)", fontSize: "0.875rem", mt: 1.5, textTransform: "none", "&:hover": { bgcolor: "rgba(249, 245, 238, 0.06)", color: "#ffffff" } }}>
            Click here if it does not start automatically
          </Button>
        </Paper>

        <ProductSuggestion currentProductID={productID} onTrial={handleExtraDownload} />
      </Container>

      <iframe title="Plugin download" style={{ display: "none" }} src={`/downloadmanager/${productID}/${os}`} />
      {/* {productID === "dd-amplifier-suite" && <Embargo handleAgree={handleStart} />} */}
    </Box>

    <BottomBar />

  </Box>
}

export default DownloadProviderPage
